import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import LineItemsEditor, { emptyRow } from "../../../stock/components/LineItemsEditor";
import { useFillMissingUnits } from "../../../stock/components/lineUnits";
import CustomerPicker from "../../components/CustomerPicker";
import DiscountFields, { DiscountedTotalsStrip, computeQuotationTotals, discountRequest } from "../../components/DiscountFields";
import SalesReferencePicker from "./SalesReferencePicker";
import RichTextEditor from "./RichTextEditor";
import { useCreateQuotationMutation, useListQuotationPrototypesQuery, useLazyGetQuotationPrototypeQuery } from "../quotationsApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

const today = () => new Date().toISOString().slice(0, 10);

const toLineRequest = (row) => ({
  lineType: row.lineType || "PRODUCT",
  productCode: row.lineType === "FREETEXT" ? null : row.productCode || null,
  description: row.productName || row.productGroupName || "", // a group line with no wording prints as the group name
  qty: Number(row.qty) || 0,
  unit: row.unit || null,
  rate: Number(row.rate) || 0,
  vatPercent: Number(row.vatPercent) || 0,
  // Batch185 - only a free-text line carries a group tag; a product has its own group.
  productGroupId: row.lineType === "FREETEXT" && row.productGroupId ? Number(row.productGroupId) : null,
});

// Modal instead of a page nested in the split-view's detail pane - same
// reasoning as PrototypeCreateModal.jsx, the Line Items table needs real
// horizontal room the detail pane can't give it.
//
// `prefill` (Batch164, "Create from lines" on an enquiry/order): { branchId,
// customerOption, salesReference, rows } applied each time the modal opens.
// Everything stays editable. `stayOnPage` skips the jump to the new quotation.
const QuotationCreateModal = ({ show, onClose, prefill = null, stayOnPage = false }) => {
  const navigate = useNavigate();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createQuotation, { isLoading: isCreating }] = useCreateQuotationMutation();
  const [fetchPrototype] = useLazyGetQuotationPrototypeQuery();
  const fillMissingUnits = useFillMissingUnits();

  const availableBranches = isAllBranches ? branches : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [customerOption, setCustomerOption] = useState(null);
  const [quotationDate, setQuotationDate] = useState(today());
  const [salesReference, setSalesReference] = useState(null);
  const [prototypeRecordGuid, setPrototypeRecordGuid] = useState("");
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([]);
  // One discount off the line total (Batch163) - lines stay as entered.
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const totals = computeQuotationTotals(rows, discountPercent, discountAmount);

  const { data: prototypes = [] } = useListQuotationPrototypesQuery(
    { branchId: branchId ? Number(branchId) : undefined, isActive: true },
    { skip: !branchId }
  );

  const customer = customerOption?.customer;
  // Set while a prefill lands, so the customer-change reset below does not
  // wipe the sales reference the prefill just put in.
  const keepReferenceRef = useRef(false);

  useEffect(() => {
    if (keepReferenceRef.current) {
      keepReferenceRef.current = false;
      return;
    }
    setSalesReference(null);
  }, [customer?.custId, customer?.branchId]);

  useEffect(() => {
    if (!show || !prefill) return;
    keepReferenceRef.current = true;
    if (prefill.branchId) setBranchId(prefill.branchId);
    if (prefill.customerOption) setCustomerOption(prefill.customerOption);
    setSalesReference(prefill.salesReference || null);
    const prefilled = prefill.rows || [];
    setRows(prefilled);
    // "Create from lines" copies an enquiry/order line's own unit, which is
    // often blank - resolve it from the Product Master (free-text lines have
    // no product and are simply skipped).
    fillMissingUnits(prefilled, prefill.branchId).then((filled) => {
      if (filled !== prefilled) setRows(filled);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const reset = () => {
    setBranchId(defaultBranchId);
    setCustomerOption(null);
    setQuotationDate(today());
    setSalesReference(null);
    setPrototypeRecordGuid("");
    setRemarks("");
    setRows([]);
    setDiscountPercent("");
    setDiscountAmount("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePrototypeChange = async (recordGuid) => {
    setPrototypeRecordGuid(recordGuid);
    if (!recordGuid) return;
    try {
      const prototype = await fetchPrototype(recordGuid).unwrap();
      const protoRows = prototype.lines.map((l) => ({
        ...emptyRow(),
        key: `proto-${l.lineId}-${Date.now()}`,
        lineType: l.lineType,
        productCode: l.productCode || "",
        productName: l.description,
        unit: l.unit || "",
        qty: String(l.qty),
        rate: String(l.rate),
        vatPercent: String(l.vatPercent),

        productGroupId: l.lineType === "FREETEXT" && l.productGroupId != null ? String(l.productGroupId) : "",
      }));
      setRows(protoRows);
      // A prototype saved before its products got a unitLabel carries blanks.
      fillMissingUnits(protoRows, branchId).then((filled) => {
        if (filled !== protoRows) setRows(filled);
      });
    } catch (e) {
      toast.error(e?.message || "Could not load the prototype");
    }
  };

  const handleSubmit = async () => {
    if (!branchId) return toast.error("Select the issuing branch");
    if (!customer) return toast.error("Select a customer");
    if (!quotationDate) return toast.error("Select a date");
    if (rows.length === 0) return toast.error("At least one line item is required");
    if (totals.exceeds) return toast.error("The discount is more than the sub total");

    try {
      const created = await createQuotation({
        ...discountRequest(discountPercent, discountAmount),
        branchId: Number(branchId),
        custId: customer.custId,
        customerBranchId: customer.branchId,
        quotationDate,
        salesEnquiryRecordGuid: salesReference?.salesEnquiryRecordGuid || null,
        salesReferenceNumber: salesReference?.salesReferenceNumber || null,
        prototypeRecordGuid: prototypeRecordGuid || null,
        remarks: remarks.trim() || null,
        lines: rows.map(toLineRequest),
      }).unwrap();
      toast.success("Quotation submitted");
      reset();
      onClose();
      if (!stayOnPage) navigate(paths.quotationDetail(created.recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not submit the quotation");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>New Quotation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-3 mb-3">
            <label className="form-label">Issuing Branch</label>
            <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Select branch</option>
              {availableBranches.map((b) => (
                <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
              ))}
            </select>
          </div>
          <div className="col-md-5 mb-3">
            <label className="form-label">Customer</label>
            <CustomerPicker value={customerOption} onChange={setCustomerOption} menuPortalTarget={document.body} />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Date</label>
            <input type="date" className="form-control" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Sales Enquiry / Order (optional)</label>
            <SalesReferencePicker
              customerBranchId={customer?.branchId}
              custId={customer?.custId}
              value={salesReference}
              onChange={setSalesReference}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Quotation Prototype (optional)</label>
            <select
              className="form-control"
              value={prototypeRecordGuid}
              onChange={(e) => handlePrototypeChange(e.target.value)}
              disabled={!branchId}
            >
              <option value="">None - build from scratch</option>
              {prototypes.map((p) => (
                <option key={p.recordGuid} value={p.recordGuid}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Footer Notes (optional) - printed after the line items, e.g. payment terms, validity, warranty</label>
          <RichTextEditor value={remarks} onChange={setRemarks} placeholder="PAYMENT TERMS: ...&#10;VALIDITY: ...&#10;WARRANTY: ..." />
        </div>

        <h6 className="mb-3">Line Items</h6>
        <LineItemsEditor
          branchId={branchId ? Number(branchId) : undefined}
          rows={rows}
          onChange={setRows}
          allowFreeText
          rateField="salesRate"
          columns={{ showRate: true, showVat: true, amountIncludesVat: true }}
        />
        <div className="row mt-3">
          <DiscountFields
            percent={discountPercent}
            amount={discountAmount}
            onPercentChange={setDiscountPercent}
            onAmountChange={setDiscountAmount}
          />
        </div>
        <DiscountedTotalsStrip totals={totals} percent={discountPercent} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isCreating}>
          {isCreating ? "Submitting..." : "Submit Quotation"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuotationCreateModal;
